import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Megaphone } from "lucide-react";
import { adminApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/input";

export function SessionsPage(): import("react").ReactElement {
  const [message, setMessage] = useState("");
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: adminApi.sessions });
  const queryClient = useQueryClient();
  const broadcast = useMutation({
    mutationFn: adminApi.broadcast,
    onSuccess: async () => {
      setMessage("");
      await queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (message.trim()) broadcast.mutate(message.trim());
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agent Sessions</h1>
          <p className="text-sm text-muted-foreground">Active channel sessions registered in KV.</p>
        </div>
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Channel</th>
                  <th className="px-4 py-3">Session</th>
                  <th className="px-4 py-3">User</th>
                </tr>
              </thead>
              <tbody>
                {sessions.data?.sessions.map((session) => (
                  <tr key={session.sessionKey} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium capitalize">{session.channel}</td>
                    <td className="px-4 py-3">{session.sessionKey}</td>
                    <td className="px-4 py-3">{session.userName}</td>
                  </tr>
                ))}
                {sessions.data?.sessions.length === 0 && (
                  <tr><td className="px-4 py-10 text-center text-muted-foreground" colSpan={3}>No active sessions.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card className="h-fit">
        <CardHeader>
          <CardTitle>Broadcast</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <Textarea value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Message active users" />
            <Button disabled={!message.trim() || broadcast.isPending}>
              <Megaphone className="h-4 w-4" />
              Send broadcast
            </Button>
            {broadcast.data && (
              <p className="text-sm text-muted-foreground">
                Sent to {broadcast.data.attempted} session(s), failed {broadcast.data.failed}.
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
